import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	ArrowRight,
	BookOpen,
	ChevronDown,
	Headphones,
	Search,
	X,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { helpHomeContent } from "./-components/helpHomeContent";

export const Route = createFileRoute("/_auth/app/_app/help/")({
	component: HelpHome,
});

const focus =
	"focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary";

function normalize(value: string) {
	return value
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase();
}

export function HelpHome() {
	const { t, i18n } = useTranslation();
	const content =
		helpHomeContent[i18n.resolvedLanguage === "en_US" ? "en_US" : "pt_BR"];
	const [query, setQuery] = useState("");
	const [showStart, setShowStart] = useState(false);
	const id = useId();
	const searchRef = useRef<HTMLInputElement>(null);
	const terms = normalize(query).trim().split(/\s+/).filter(Boolean);
	const searching = terms.length > 0;
	const matches = (text: string) =>
		terms.every((term) => normalize(text).includes(term));
	const topics = content.topics.filter((topic) =>
		matches(
			[
				topic.title,
				topic.category,
				topic.description,
				...topic.steps,
				topic.tip,
				topic.keywords,
			].join(" "),
		),
	);
	const faqs = content.faq.filter((faq) =>
		matches(`${faq.question} ${faq.answer}`),
	);
	const count = topics.length + faqs.length;
	const onlyFaqResults = searching && topics.length === 0 && faqs.length > 0;

	function clearSearch() {
		setQuery("");
		searchRef.current?.focus();
	}

	return (
		<div
			className="mx-auto max-w-6xl px-4 pb-12 sm:px-8 lg:px-12"
			data-testid="help-main-content"
		>
			<nav
				aria-label={t("help.title")}
				className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-b border-base-300 py-3 text-sm"
			>
				<Link
					to="/app"
					className={`inline-flex min-h-11 items-center gap-2 rounded-sm text-base-content/75 hover:text-primary ${focus}`}
				>
					<ArrowLeft className="size-4" aria-hidden="true" />
					{t("help.home.backToApp")}
				</Link>
				<div className="flex items-center gap-5">
					<Link
						to="/app/help/user-guide"
						className={`inline-flex min-h-11 items-center gap-2 rounded-sm hover:text-primary ${focus}`}
					>
						<BookOpen className="size-4" aria-hidden="true" />
						{t("help.userGuide")}
					</Link>
					<Link
						to="/app/help/support"
						className={`inline-flex min-h-11 items-center gap-2 rounded-sm hover:text-primary ${focus}`}
					>
						<Headphones className="size-4" aria-hidden="true" />
						{t("help.support")}
					</Link>
				</div>
			</nav>
			<header className="py-8 sm:py-10" data-testid="help-header">
				<h1
					className="text-3xl font-bold tracking-tight text-base-content sm:text-4xl"
					data-testid="help-page-title"
				>
					{content.title}
				</h1>
				<p
					className="mt-3 max-w-2xl text-base leading-relaxed text-base-content/75"
					data-testid="help-page-description"
				>
					{content.description}
				</p>
				<search className="mt-6 max-w-2xl">
					<label htmlFor={`${id}-help-search`} className="sr-only">
						{content.searchLabel}
					</label>
					<div className="flex min-h-14 items-center gap-3 rounded-field border border-base-content/30 bg-base-100 px-4 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary">
						<Search
							className="size-5 shrink-0 text-primary"
							aria-hidden="true"
						/>
						<input
							ref={searchRef}
							id={`${id}-help-search`}
							type="search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Escape") clearSearch();
							}}
							placeholder={content.searchPlaceholder}
							aria-controls={`${id}-help-results`}
							className="min-w-0 flex-1 bg-transparent py-4 text-base text-base-content outline-none placeholder:text-base-content/65 [&::-webkit-search-cancel-button]:appearance-none"
						/>
						{query && (
							<button
								type="button"
								aria-label={content.clearSearch}
								onClick={clearSearch}
								className={`-mr-2 flex size-11 shrink-0 items-center justify-center rounded-field hover:bg-base-200 ${focus}`}
							>
								<X className="size-5" aria-hidden="true" />
							</button>
						)}
					</div>
					<output
						aria-live="polite"
						className="mt-2 block min-h-5 text-sm text-base-content/70"
					>
						{searching
							? (count === 1 ? content.resultOne : content.results).replace(
									"{{count}}",
									String(count),
								)
							: t("help.home.searchHint")}
					</output>
				</search>
			</header>
			{!searching && (
				<section
					aria-labelledby={`${id}-help-start-title`}
					className="mb-10 rounded-box bg-primary/7 p-5 sm:p-7"
					data-testid="help-getting-started-section"
				>
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div className="w-full sm:w-auto sm:max-w-2xl">
							<h2
								id={`${id}-help-start-title`}
								className="text-xl font-semibold"
							>
								<span className="hidden sm:inline">{content.startTitle}</span>
								<button
									type="button"
									aria-expanded={showStart}
									aria-controls={`${id}-help-start-steps`}
									onClick={() => setShowStart(!showStart)}
									className={`flex min-h-11 w-full items-center justify-between gap-4 rounded-sm text-left sm:hidden ${focus}`}
								>
									{content.startTitle}
									<ChevronDown
										className={`size-5 shrink-0 text-primary ${showStart ? "rotate-180" : ""}`}
										aria-hidden="true"
									/>
								</button>
							</h2>
							<p
								className={`mt-2 text-sm leading-relaxed text-base-content/75 ${showStart ? "" : "hidden sm:block"}`}
							>
								{content.startBody}
							</p>
						</div>
						<Link
							to="/app/help/user-guide/establishments"
							className={`${showStart ? "inline-flex" : "hidden sm:inline-flex"} min-h-11 items-center gap-2 rounded-field bg-primary px-4 text-sm font-semibold text-primary-content hover:bg-primary/90 ${focus}`}
						>
							{content.startAction}
							<ArrowRight className="size-4" aria-hidden="true" />
						</Link>
					</div>
					<ol
						id={`${id}-help-start-steps`}
						className={`mt-6 gap-5 sm:grid sm:grid-cols-3 ${showStart ? "grid" : "hidden"}`}
						data-testid="help-getting-started-steps"
					>
						{content.startSteps.map((step, index) => (
							<li key={step.title} className="flex items-start gap-3">
								<span
									className="flex size-7 shrink-0 items-center justify-center rounded-full border border-primary/30 text-sm font-semibold text-primary"
									aria-hidden="true"
								>
									{index + 1}
								</span>
								<div>
									<h3 className="text-base font-semibold">{step.title}</h3>
									<p className="mt-1 text-sm leading-relaxed text-base-content/75">
										{step.body}
									</p>
								</div>
							</li>
						))}
					</ol>
				</section>
			)}
			<div
				id={`${id}-help-results`}
				className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] lg:gap-12"
			>
				{!onlyFaqResults && (
					<section
						aria-labelledby={`${id}-help-topics-title`}
						className="min-w-0"
					>
						<h2
							id={`${id}-help-topics-title`}
							className="text-2xl font-semibold"
						>
							{content.topicsTitle}
						</h2>
						<p className="mt-2 text-sm leading-relaxed text-base-content/75">
							{content.topicsDescription}
						</p>
						{searching && count === 0 ? (
							<div className="mt-6 rounded-box border border-base-300 bg-base-100 p-6">
								<h3 className="text-lg font-semibold">
									{content.noResultsTitle}
								</h3>
								<p className="mt-2 text-sm leading-relaxed text-base-content/75">
									{content.noResultsBody}
								</p>
								<button
									type="button"
									onClick={clearSearch}
									className={`mt-4 min-h-11 rounded-field bg-primary px-4 text-sm font-medium text-primary-content hover:bg-primary/90 ${focus}`}
								>
									{content.browseAll}
								</button>
							</div>
						) : (
							<div className="mt-5 border-t border-base-300">
								{topics.map((topic) => (
									<details
										key={`${topic.id}-${searching}`}
										open={searching || undefined}
										className="group border-b border-base-300"
										data-testid={`help-topic-${topic.id}`}
									>
										<summary
											className={`flex cursor-pointer list-none items-center justify-between gap-4 rounded-sm py-4 hover:text-primary [&::-webkit-details-marker]:hidden ${focus}`}
										>
											<span>
												<span className="block text-base font-semibold">
													{topic.title}
												</span>
												<span className="mt-1 block text-sm text-base-content/70">
													{topic.category}
												</span>
											</span>
											<ChevronDown
												className="size-5 shrink-0 text-primary group-open:rotate-180"
												aria-hidden="true"
											/>
										</summary>
										<div className="pb-6 text-sm leading-relaxed">
											<p className="max-w-prose text-base-content/80">
												{topic.description}
											</p>
											<ol className="mt-4 list-decimal space-y-3 pl-5 marker:font-semibold marker:text-primary">
												{topic.steps.map((step) => (
													<li key={step} className="pl-1">
														{step}
													</li>
												))}
											</ol>
											<p className="mt-4 rounded-field bg-base-200 p-3 text-base-content/80">
												{topic.tip}
											</p>
											<Link
												to={topic.guideTo}
												className={`mt-3 inline-flex min-h-11 items-center gap-2 rounded-sm font-semibold text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary ${focus}`}
											>
												{content.guideLink}
												<span className="sr-only">: {topic.category}</span>
												<ArrowRight className="size-4" aria-hidden="true" />
											</Link>
										</div>
									</details>
								))}
							</div>
						)}
						<Link
							to="/app/help/user-guide"
							className={`mt-4 inline-flex min-h-11 items-center gap-2 rounded-sm text-sm font-semibold text-primary hover:underline ${focus}`}
						>
							{t("help.home.allGuides")}
							<ArrowRight className="size-4" aria-hidden="true" />
						</Link>
					</section>
				)}
				<div
					className={
						onlyFaqResults
							? "grid min-w-0 items-start gap-8 lg:col-span-2 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]"
							: "min-w-0 space-y-8"
					}
				>
					{faqs.length > 0 && (
						<section aria-labelledby={`${id}-help-faq-title`}>
							<h2 id={`${id}-help-faq-title`} className="text-xl font-semibold">
								{content.faqTitle}
							</h2>
							<div className="mt-5 border-t border-base-300">
								{faqs.map((faq) => (
									<details
										key={`${faq.id}-${searching}`}
										open={searching || undefined}
										className="group border-b border-base-300"
									>
										<summary
											className={`flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 rounded-sm py-4 text-sm font-semibold hover:text-primary [&::-webkit-details-marker]:hidden ${focus}`}
										>
											<span>{faq.question}</span>
											<ChevronDown
												className="size-4 shrink-0 text-primary group-open:rotate-180"
												aria-hidden="true"
											/>
										</summary>
										<p className="pb-5 text-sm leading-relaxed text-base-content/80">
											{faq.answer}
										</p>
									</details>
								))}
							</div>
						</section>
					)}
					<section
						aria-labelledby={`${id}-help-support-title`}
						className="rounded-box bg-neutral p-6 text-neutral-content"
					>
						<Headphones
							className="mb-4 size-7 text-secondary"
							aria-hidden="true"
						/>
						<h2
							id={`${id}-help-support-title`}
							className="text-xl font-semibold"
						>
							{content.supportTitle}
						</h2>
						<p className="mt-3 text-sm leading-relaxed text-neutral-content/85">
							{content.supportBody}
						</p>
						<Link
							to="/app/help/support"
							className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-field bg-secondary px-4 text-sm font-semibold text-secondary-content hover:bg-secondary/90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-secondary"
						>
							{content.supportAction}
							<ArrowRight className="size-4" aria-hidden="true" />
						</Link>
						<Link
							to="/app/help/support/tickets"
							className="mt-2 flex min-h-11 w-fit items-center rounded-sm text-sm underline underline-offset-4 hover:text-secondary focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-secondary"
						>
							{content.ticketsAction}
						</Link>
					</section>
				</div>
			</div>
		</div>
	);
}
